import { useToast } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import AwsS3 from '@uppy/aws-s3';
import { Uppy } from '@uppy/core';
import { useUppy } from '@uppy/react';
import { useSyncNodeSecurity } from 'apps/TenantApp/hooks/useNodeSecurity';
import { useCallback, useState } from 'react';
import { graphql, useMutation } from 'utils/graphClient';
import { UploadFile } from '.';
import { useVaultUploader_copyDraftVaultItem_Mutation } from './__generated__/useVaultUploader_copyDraftVaultItem_Mutation.graphql';
import { useVaultUploader_createDraftVaultItem_Mutation } from './__generated__/useVaultUploader_createDraftVaultItem_Mutation.graphql';
import { useVaultUploader_deleteDraftVaultItem_Mutation } from './__generated__/useVaultUploader_deleteDraftVaultItem_Mutation.graphql';
import { useVaultUploader_markDraftVaultItemAsUploaded_Mutation } from './__generated__/useVaultUploader_markDraftVaultItemAsUploaded_Mutation.graphql';
import { useVaultUploader_assignDraftVaultItem_Mutation } from './__generated__/useVaultUploader_assignDraftVaultItem_Mutation.graphql';
import { useFeatureFlag } from 'utils/sessionProvider';
import { ONE_GIGABYTE_IN_BYTES } from 'utils/constants';

const markDraftVaultItemAsUploadedMutation = graphql`
  mutation useVaultUploader_markDraftVaultItemAsUploaded_Mutation(
    $draftVaultItemId: ID!
    $file: FileInput!
  ) {
    markDraftVaultItemAsUploaded(
      input: { draftVaultItemId: $draftVaultItemId, file: $file }
    ) {
      draftVaultItemUpdated {
        id
        name
        content(returnUploadingFile: true) {
          ... on File {
            id
            name
            mimeType
            persistenceState
          }
        }
        ...VaultUploaderFileCard_draftItem
      }
    }
  }
`;

const createDraftVaultItemMutation = graphql`
  mutation useVaultUploader_createDraftVaultItem_Mutation(
    $draftUploadGroupId: ID!
    $fileName: String!
    $fileType: String!
    $fileSize: BigInt!
    $asCover: Boolean
  ) {
    createDraftVaultItem(
      input: {
        draftUploadGroupId: $draftUploadGroupId
        fileName: $fileName
        fileType: $fileType
        fileSize: $fileSize
        cover: $asCover
      }
    ) {
      draftUploadGroup {
        id
        items {
          edges {
            node {
              id
              name
              cover
              content(returnUploadingFile: true) {
                ... on File {
                  id
                  name
                  mimeType
                  persistenceState
                }
              }
              ...VaultUploaderFileCard_draftItem
            }
          }
        }
      }
      draftVaultItemCreated {
        id
        cover
        content(returnUploadingFile: true) {
          ... on File {
            id
            name
            mimeType
            persistenceState
          }
        }
      }
      uploadSignature {
        uploadToken
        signature {
          method
          url
          fields
          headers
        }
      }
    }
  }
`;

const copyDraftVaultItemMutation = graphql`
  mutation useVaultUploader_copyDraftVaultItem_Mutation(
    $draftVaultItemId: ID!
    $vaultId: ID
  ) {
    copyDraftVaultItem(
      input: { draftVaultItemId: $draftVaultItemId, vaultId: $vaultId }
    ) {
      draftUploadGroup {
        id
        items {
          edges {
            node {
              id
              name
              content(returnUploadingFile: true) {
                ... on File {
                  id
                  name
                  mimeType
                  persistenceState
                }
              }
              ...VaultUploaderFileCard_draftItem
            }
          }
        }
      }
      draftVaultItemCreated {
        id
        content(returnUploadingFile: true) {
          ... on File {
            id
            name
            mimeType
            persistenceState
          }
        }
      }
    }
  }
`;

const deleteDraftVaultItemMutation = graphql`
  mutation useVaultUploader_deleteDraftVaultItem_Mutation(
    $draftVaultItemIds: [ID!]!
  ) {
    deleteDraftVaultItems(input: { draftVaultItemIds: $draftVaultItemIds }) {
      draftVaultItemDeletedIds
      draftUploadGroup {
        id
        items {
          edges {
            node {
              id
              name
              content {
                ... on File {
                  id
                  name
                  mimeType
                }
              }
              ...VaultUploaderFileCard_draftItem
            }
          }
        }
      }
    }
  }
`;

const testQuery = graphql`
  mutation useVaultUploader_assignDraftVaultItem_Mutation(
    $draftVaultItemId: ID!
    $vaultId: ID!
  ) {
    assignDraftVaultItem(
      input: { draftVaultItemId: $draftVaultItemId, vaultId: $vaultId }
    ) {
      draftVaultItemUpdated {
        id
        name
        content(returnUploadingFile: true) {
          ... on File {
            id
            name
            mimeType
            persistenceState
          }
        }
        ...VaultUploaderFileCard_draftItem
      }
    }
  }
`;

interface UseVaultUploaderResponse {
  allFiles: UploadFile[];
  failedFileIds: string[];
  uploadProgress: number;

  retryUploads(fileIds?: string[]): void;

  cancelUpload(fileId: string): void;

  removeDraftVaultItem(draftVaultItemId: string): void;

  reset(): void;

  uploadFiles(files: File[], asCover?: boolean): void;

  copyDraftVaultItem(
    draftVaultItemId: string,
    vaultId: string | undefined,
    isDownloadPrevented: boolean,
    isWatermarkAdded: boolean,
  ): void;
}

export function useVaultUploader(
  draftUploadGroupId: string,
  vaultId: string | undefined,
): UseVaultUploaderResponse {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState<UploadFile[]>([]);
  const { syncSecuritySettings } = useSyncNodeSecurity();

  const toast = useToast();

  const allowUploadWithinSingleVault = useFeatureFlag(
    'FEATURE_ALLOW_UPLOAD_WITHIN_VAULT',
  );
  const isAllowedToUploadToSingleVault = Boolean(
    allowUploadWithinSingleVault && vaultId,
  );

  const [createDraftVaultItem] =
    useMutation<useVaultUploader_createDraftVaultItem_Mutation>(
      createDraftVaultItemMutation,
    );

  const [copyDraftVaultItem] =
    useMutation<useVaultUploader_copyDraftVaultItem_Mutation>(
      copyDraftVaultItemMutation,
    );

  const [markDraftVaultItemAsUploaded] =
    useMutation<useVaultUploader_markDraftVaultItemAsUploaded_Mutation>(
      markDraftVaultItemAsUploadedMutation,
    );

  const [deleteDraftVaultItem] =
    useMutation<useVaultUploader_deleteDraftVaultItem_Mutation>(
      deleteDraftVaultItemMutation,
    );

  const [linkToVault] =
    useMutation<useVaultUploader_assignDraftVaultItem_Mutation>(testQuery);

  const uppy = useUppy(() => {
    const getUploadParameters: (file: UploadFile) => any = (file) => {
      return new Promise((resolve, reject) => {
        createDraftVaultItem({
          variables: {
            draftUploadGroupId,
            fileName: file.meta.originalFileName || file.name,
            fileType: file.type || '',
            fileSize: file.size,
            asCover: file.meta.asCover,
          },
          onCompleted: (data) => {
            if (data.createDraftVaultItem?.uploadSignature) {
              uppy.setFileMeta(file.id, {
                uploadToken:
                  data.createDraftVaultItem.uploadSignature.uploadToken,
                draftVaultItemId:
                  data.createDraftVaultItem.draftVaultItemCreated.id,
              });
              updateProgressState();
              resolve(data.createDraftVaultItem.uploadSignature.signature);
            } else {
              reject(t`Something went wrong while uploading a file`);
            }
          },
        });
      });
    };

    const updateProgressState = () => {
      setUploadingFiles(uppyInstance.getFiles()); // force state update of file progress attribute
    };
    const uppyInstance = new Uppy({
      meta: { type: 'avatar' },
      debug: true,
      restrictions: {
        maxFileSize: ONE_GIGABYTE_IN_BYTES,
      },
      autoProceed: true,
    });
    uppyInstance.use(AwsS3, {
      getUploadParameters,
    });

    uppyInstance.on('progress', (progress) => {
      setUploadProgress(progress);
      updateProgressState();
    });
    uppyInstance.on('complete', updateProgressState);
    uppyInstance.on('upload', updateProgressState);
    uppyInstance.on('error', (e) => {
      uploadingFiles.forEach((f) => markAsFailedFile(f, e));
      updateProgressState();
    });
    uppyInstance.on('upload-progress', updateProgressState);
    uppyInstance.on('upload-success', (file: UploadFile, response) => {
      const fileExists =
        uppy.getFiles().filter((f) => f.id === file.id).length > 0;
      if (fileExists && file.meta.draftVaultItemId && file.meta.uploadToken) {
        markDraftVaultItemAsUploaded({
          variables: {
            draftVaultItemId: file.meta.draftVaultItemId,
            file: { uploadToken: file.meta.uploadToken },
          },
          onCompleted: () => {
            markAsUploadedFile(file);

            const draftVaultItemId = file.meta.draftVaultItemId;
            if (isAllowedToUploadToSingleVault && draftVaultItemId && vaultId) {
              linkToVault({
                variables: {
                  draftVaultItemId,
                  vaultId,
                },
              });
            }
          },
          onError: (error) => {
            markAsFailedFile(file, error);
          },
        });
      }
      updateProgressState();
    });
    uppyInstance.on('upload-retry', updateProgressState);
    uppyInstance.on('upload-error', (file, error) => {
      markAsFailedFile(file, error);
      updateProgressState();
    });
    uppyInstance.on('file-added', updateProgressState);
    uppyInstance.on('files-added', updateProgressState);
    uppyInstance.on('file-removed', updateProgressState);

    return uppyInstance;
  });

  const retryUploads = (fileIds?: string[]) => {
    if (!fileIds) {
      uppy.retryAll();
      setUploadingFiles(uppy.getFiles());
      uppy.setState({ failedFileIds: [] });
    } else {
      fileIds.forEach((fileId) => {
        uppy.retryUpload(fileId);
        unmarkAsFailedFile(fileId);
      });
    }
  };

  const getFailedFileIds: () => string[] = () => {
    return uppy.getState().failedFileIds || [];
  };

  const markAsUploadedFile = (file: UploadFile) => {
    uppy.setFileMeta(file.id, {
      markAsUploaded: true,
    });
    uppy.removeFile(file.id);
  };

  const markAsFailedFile = (file: UploadFile, error: Error) => {
    uppy.setState({
      failedFileIds: Array.from(new Set([...getFailedFileIds(), file.id])),
    });
    uppy.setFileMeta(file.id, {
      error,
      statusCodeError: file.response?.status,
    });
  };

  const unmarkAsFailedFile = (fileId: string) => {
    uppy.setState({
      failedFileIds: getFailedFileIds().filter((fId) => fId !== fileId),
    });
    uppy.setFileMeta(fileId, {
      error: undefined,
      statusCodeError: undefined,
    });
  };

  const removeDraftVaultItem = (draftVaultItemId: string) => {
    deleteDraftVaultItem({
      variables: { draftVaultItemIds: [draftVaultItemId] },
    });
  };

  const cancelUpload = (fileId: string) => {
    const file: UploadFile = uppy.getFile(fileId);
    if (file?.meta?.draftVaultItemId) {
      deleteDraftVaultItem({
        variables: { draftVaultItemIds: [file.meta.draftVaultItemId] },
      });
    }
    uppy.removeFile(fileId);
    unmarkAsFailedFile(fileId);
  };

  const reset = useCallback(() => {
    uppy.reset();
    setUploadProgress(0);
    setUploadingFiles([]);
  }, [uppy]);

  const uploadFiles = (files: File[], asCover?: boolean) => {
    files.forEach((selectedFile) => {
      try {
        uppy.addFile({
          source: 'file input',
          name: getRandomNum() + '-' + selectedFile.name, // creates a unique name to allow multiple uploads of same file
          type: selectedFile.type,
          data: selectedFile,
          meta: {
            originalFileName: selectedFile.name,
            asCover,
          },
        });
      } catch (err) {
        if (
          err instanceof Error &&
          err.message.includes('This file exceeds maximum allowed size')
        ) {
          toast({
            title: t`File is too large. Please limit uploads to 1 Gigabyte`,
            status: 'error',
          });
        } else {
          toast({
            title: t`Something went wrong uploading a file`,
            status: 'error',
          });
        }
      }
    });
  };

  return {
    allFiles: uploadingFiles,
    removeDraftVaultItem,
    failedFileIds: getFailedFileIds(),
    uploadProgress,
    retryUploads,
    cancelUpload,
    reset,
    uploadFiles,
    copyDraftVaultItem: (
      draftVaultItemId,
      vaultId,
      isDownloadPrevented,
      isWatermarkAdded,
    ) => {
      copyDraftVaultItem({
        variables: { draftVaultItemId, vaultId },
        onCompleted: (response) => {
          const copiedVaultItemId =
            response.copyDraftVaultItem?.draftVaultItemCreated.id;
          if (!copiedVaultItemId) {
            return;
          }
          syncSecuritySettings({ isDownloadPrevented, isWatermarkAdded }, [
            copiedVaultItemId,
          ]);
        },
      });
    },
  };
}

function getRandomNum() {
  return '' + Math.floor(Math.random() * 1000);
}
